import { SidebarItemKind } from '../domain/enums.js';
import { ExportFormat } from '../jobs/rows-jobs.js';
import { z } from '../zod.js';
import { IsoTimestamp, JsonValue, Uuid } from './common.js';
import { queryInt } from './query.js';
/**
 * The workspace shapes of exit 1a (1.0b deliverable 18): the Recent list, the sidebar, settings
 * writes, rendering (1.W) and exports (1.P).
 */
/** `docs.recent`: the canon documents opened last, at most `limit` (a bounded list). */
export const RecentQueryV1 = z.strictObject({ limit: queryInt(1, 50).default(20) });
const Label = z.string().min(1).max(200);
/** A sidebar entry (`sidebar_item`): a pinned document or view, or a group. */
export const SidebarItemV1 = z.strictObject({
    id: Uuid,
    parentId: Uuid.nullable(),
    kind: SidebarItemKind,
    /** The pinned document or view; null for a group. */
    refId: Uuid.nullable(),
    label: Label.nullable(),
    sortKey: z.string().min(1).max(200).nullable(),
});
/** `sidebar.add`. An add appends to its group (the top level when `parentId` is omitted). */
export const SidebarAddRequestV1 = z.strictObject({
    kind: SidebarItemKind,
    refId: Uuid.optional(),
    label: Label.optional(),
    parentId: Uuid.optional(),
});
/** `sidebar.update`: any of these; `afterId` is the entry it follows (null: first). */
export const SidebarUpdateRequestV1 = z.strictObject({
    label: Label.nullable().optional(),
    parentId: Uuid.nullable().optional(),
    afterId: Uuid.nullable().optional(),
});
/**
 * `settings.update`'s path: any key of a setting's shape, so a key outside `SETTINGS` reaches the
 * handler, which answers 404; a value its key's schema refuses answers 400 `invalid_request`.
 */
export const SettingParams = z.strictObject({ key: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/) });
export const SettingUpdateRequestV1 = z.strictObject({ value: JsonValue });
/** What `render.doc` renders: GM Read mode, the table-read (secrets and GM notes stripped), Markdown. */
export const RENDER_TARGETS = ['html', 'table', 'markdown'];
export const RenderTarget = z.enum(RENDER_TARGETS);
export const RenderQueryV1 = z.strictObject({ target: RenderTarget });
/** A loss the renderer reported (doc-model's render warning codes). */
export const RenderWarningV1 = z.strictObject({
    code: z.string().regex(/^[a-z][a-z0-9_]*$/),
    message: z.string(),
});
/** A rendered document: HTML for `html` and `table`, text for `markdown`. */
export const RenderedV1 = z.discriminatedUnion('target', [
    z.strictObject({
        target: z.enum(['html', 'table']),
        html: z.string(),
        warnings: z.array(RenderWarningV1),
    }),
    z.strictObject({
        target: z.literal('markdown'),
        text: z.string(),
        warnings: z.array(RenderWarningV1),
    }),
]);
/** `exports.create`: an export on demand (decision 32; there is no nightly export). */
export const ExportCreateRequestV1 = z.strictObject({ format: ExportFormat });
/** Where an export's job stands. */
export const EXPORT_STATUSES = ['queued', 'running', 'succeeded', 'failed'];
export const ExportStatus = z.enum(EXPORT_STATUSES);
/**
 * An export: its record is the `export.run` job's state and its file an `asset` of kind `export`
 * (orchestrator ruling 8), so it needs no table of its own.
 */
export const ExportV1 = z.strictObject({
    /** The job's id. */
    id: Uuid,
    format: ExportFormat,
    status: ExportStatus,
    requestedAt: IsoTimestamp,
    finishedAt: IsoTimestamp.nullable(),
    /** The `asset` the job writes; null until it succeeds. */
    assetId: Uuid.nullable(),
    bytes: z.number().int().nonnegative().nullable(),
    /**
     * The pages written as a placeholder line because their body could not be rendered: the
     * `unrenderedPages` of the zip's `manifest.json` (CS r2b; DECISIONS 133, 1.V-2 open point 2).
     * Null until the export succeeds, and whenever the manifest cannot be read. Always 0 for a
     * JSON export, which renders no page.
     */
    unrenderedPages: z.number().int().nonnegative().nullable(),
});
//# sourceMappingURL=workspace.js.map